import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  Tooltip
} from '@mui/material';
import NoteIcon from '@mui/icons-material/Note';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';

export interface TodoItem {
  id: number;
  text: string;
  dueDate: string;
}

interface TodoListProps {
  title?: string;
}

const TodoList: React.FC<TodoListProps> = ({ title = "To-Do List" }) => {
  const [todoItems, setTodoItems] = useState<TodoItem[]>([
    { id: 1, text: 'Complete deal review for #12345', dueDate: 'May 20, 2025' },
    { id: 2, text: 'Follow up on pending documents for #54321', dueDate: 'May 18, 2025' },
    { id: 3, text: 'Schedule client meeting for new deals', dueDate: 'May 22, 2025' }
  ]);
  const [editingTodoId, setEditingTodoId] = useState<number | null>(null);
  const [newTodoText, setNewTodoText] = useState('');
  const [newTodoDueDate, setNewTodoDueDate] = useState('');
  const [isAddingTodo, setIsAddingTodo] = useState(false);

  const handleAddTodo = () => {
    setIsAddingTodo(true);
    setNewTodoText('');
    setNewTodoDueDate('');
  };

  const handleSaveTodo = () => {
    if (newTodoText) {
      const newId = Math.max(0, ...todoItems.map(i => i.id)) + 1;
      setTodoItems([...todoItems, { id: newId, text: newTodoText, dueDate: newTodoDueDate }]);
      setNewTodoText('');
      setNewTodoDueDate('');
      setIsAddingTodo(false);
    }
  };

  const handleEditTodo = (id: number) => {
    const todoItem = todoItems.find(todo => todo.id === id);
    if (todoItem) {
      setEditingTodoId(id);
      setNewTodoText(todoItem.text);
      setNewTodoDueDate(todoItem.dueDate);
    }
  };

  const handleUpdateTodo = (id: number) => {
    if (newTodoText) {
      const updatedItems = [...todoItems];
      const itemIndex = updatedItems.findIndex(i => i.id === id);
      updatedItems[itemIndex] = { ...updatedItems[itemIndex], text: newTodoText, dueDate: newTodoDueDate };
      setTodoItems(updatedItems);
      setEditingTodoId(null);
    }
  };

  const handleDeleteTodo = (id: number) => {
    setTodoItems(todoItems.filter(i => i.id !== id));
  };

  const handleClearAll = () => {
    setTodoItems([]);
  };

  return (
    <Card elevation={3} sx={{ mb: 3 }}>
      <CardHeader 
        title={title} 
        titleTypographyProps={{ variant: 'h6' }}
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="New Item">
              <IconButton 
                color="primary" 
                size="small" 
                sx={{ bgcolor: 'rgba(25, 118, 210, 0.08)', '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.12)' } }}
                onClick={handleAddTodo}
              >
                <NoteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete All">
              <IconButton 
                color="error" 
                size="small" 
                sx={{ bgcolor: 'rgba(211, 47, 47, 0.08)', '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.12)' } }}
                onClick={handleClearAll}
                disabled={todoItems.length === 0}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />
      <Divider />
      <CardContent>
        <List dense>
          {todoItems.map((item, index) => (
            <React.Fragment key={item.id}>
              <ListItem sx={{ height: '15px', py: 0 }}>
                {editingTodoId === item.id ? (
                  <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', gap: 1 }}>
                    <TextField
                      size="small"
                      value={newTodoText}
                      onChange={(e) => setNewTodoText(e.target.value)}
                      placeholder="Task description"
                      sx={{ flex: 3, '& .MuiInputBase-root': { height: '24px', fontSize: '0.75rem' } }}
                    />
                    <TextField
                      size="small"
                      value={newTodoDueDate}
                      onChange={(e) => setNewTodoDueDate(e.target.value)}
                      placeholder="Due date"
                      sx={{ flex: 1, '& .MuiInputBase-root': { height: '24px', fontSize: '0.75rem' } }}
                    />
                    <IconButton 
                      size="small" 
                      color="primary" 
                      onClick={() => handleUpdateTodo(item.id)}
                      sx={{ padding: '1px' }}
                    >
                      <SaveIcon sx={{ fontSize: '0.75rem' }} />
                    </IconButton>
                  </Box>
                ) : (
                  <>
                    <ListItemText 
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="body2" noWrap sx={{ fontSize: '0.75rem' }}>{item.text}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Due: {item.dueDate}</Typography>
                        </Box>
                      } 
                    />
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                      <IconButton 
                        size="small" 
                        color="primary" 
                        onClick={() => handleEditTodo(item.id)}
                        sx={{ padding: '1px' }}
                      >
                        <EditIcon sx={{ fontSize: '0.75rem' }} />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error" 
                        onClick={() => handleDeleteTodo(item.id)}
                        sx={{ padding: '1px' }}
                      >
                        <DeleteIcon sx={{ fontSize: '0.75rem' }} />
                      </IconButton>
                    </Box>
                  </>
                )}
              </ListItem>
              {index < todoItems.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
          
          {isAddingTodo && (
            <>
              {todoItems.length > 0 && <Divider component="li" />}
              <ListItem sx={{ height: '15px', py: 0 }}>
                <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', gap: 1 }}>
                  <TextField
                    size="small"
                    value={newTodoText}
                    onChange={(e) => setNewTodoText(e.target.value)}
                    placeholder="Task description"
                    sx={{ flex: 3, '& .MuiInputBase-root': { height: '24px', fontSize: '0.75rem' } }}
                  />
                  <TextField
                    size="small"
                    value={newTodoDueDate}
                    onChange={(e) => setNewTodoDueDate(e.target.value)}
                    placeholder="Due date"
                    sx={{ flex: 1, '& .MuiInputBase-root': { height: '24px', fontSize: '0.75rem' } }}
                  />
                  <IconButton 
                    size="small" 
                    color="primary" 
                    onClick={handleSaveTodo}
                    sx={{ padding: '1px' }}
                  >
                    <SaveIcon sx={{ fontSize: '0.75rem' }} />
                  </IconButton>
                </Box>
              </ListItem>
            </>
          )}
          
          {todoItems.length === 0 && !isAddingTodo && (
            <Box p={1} display="flex" justifyContent="center">
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                No to-do items available
              </Typography>
            </Box>
          )}
        </List>
      </CardContent>
    </Card>
  );
};

export default TodoList;
